import type {
  AlertStatus,
  ChakraProps,
  ThemeTypings,
  ToastId,
  UseToastOptions,
} from '@chakra-ui/react';
import {
  Box,
  CloseButton,
  Flex,
  Link,
  useToast as chakraUseToast,
} from '@chakra-ui/react';
import { getAssetUrl, getChakraVarExpression } from '@cronos-app/chakra';
import { useIsDesktop } from '@cronos-app/components';
import type { ChainId } from '@cronos-app/sdk';
import {
  currentWallet,
  getAddEthereumChainParameter,
} from '@cronos-app/wallet';
import { MaskIcon } from '@ui/components/MaskIcon';
import { TOAST_DURATION_MS } from '@ui/configs/ui';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';

type ToastOptions = UseToastOptions & {
  txId?: string;
};

const statusColors: Record<AlertStatus, ThemeTypings['colors']> = {
  success: 'brand.secondary-green',
  error: 'brand.error',
  info: 'brand.white',
  loading: 'brand.white',
  warning: 'brand.warn',
};

const statusIcons: Record<AlertStatus, FC<ChakraProps>> = {
  success: (props) => {
    return (
      <MaskIcon
        icon={`url(${getAssetUrl('/masks/success.svg')})`}
        boxSize="24px"
        {...props}
      />
    );
  },
  error: (props) => {
    return (
      <MaskIcon
        icon={`url(${getAssetUrl('/masks/error.svg')})`}
        boxSize="24px"
        {...props}
      />
    );
  },
  info: () => null,
  loading: () => null,
  warning: () => null,
};

type RenderProps = ToastOptions & {
  onClose(): void;
};
const Render = ({
  onClose,
  description,
  title,
  isClosable,
  txId,
  status,
}: RenderProps) => {
  const t = useTranslations();
  const chainId: ChainId | undefined = currentWallet.useChainId();
  const StatusIcon = status ? statusIcons[status] : undefined;
  const statusColor: ThemeTypings['colors'] = status
    ? statusColors[status]
    : 'brand.white';

  const explorerLink = useMemo(() => {
    return txId && chainId
      ? `${
          getAddEthereumChainParameter(chainId).blockExplorerUrls?.[0]
        }/tx/${txId}`
      : undefined;
  }, [chainId, txId]);

  return (
    <Flex
      flex="1"
      py={{
        base: '12px',
        desktop: '16px',
      }}
      px="16px"
      color="brand.white"
      bg={[
        `${getChakraVarExpression(
          'colors',
          'gradients.dark-blue',
        )} padding-box`,
        `linear-gradient(to right, ${getChakraVarExpression(
          'colors',
          statusColor,
        )}, #002d73) border-box`,
      ].join(',')}
      rounded="8px"
      borderWidth="1px"
      borderColor="transparent"
    >
      {StatusIcon && <StatusIcon flexShrink="0" bg={statusColor} mr="16px" />}
      <Flex flex="1" gap={1} flexDirection="column">
        {title ? (
          <Box
            textStyle={{
              base: 'body2Bold',
              desktop: 'body1Bold',
            }}
            textTransform="capitalize"
            color={statusColor}
          >
            {title}
          </Box>
        ) : null}
        {description ? (
          <Box
            textStyle={{
              base: 'body2',
              desktop: 'body1',
            }}
          >
            {description}
          </Box>
        ) : null}
        {explorerLink && (
          <Link
            href={explorerLink}
            isExternal
            color="brand.secondary-sky-blue"
            textStyle="cta"
            textTransform="capitalize"
          >
            {t('card.view on explorer')}
          </Link>
        )}
      </Flex>
      {isClosable !== false && (
        <CloseButton
          flexShrink="0"
          boxSize="24px"
          fontSize="12px"
          onClick={onClose}
          ml="4px"
        />
      )}
    </Flex>
  );
};

/**
 * The type `ToastInterface` should be just `typeof chakraToast, but there's a typescript bug
 * https://github.com/microsoft/TypeScript/issues/42873
 */
export interface ToastInterface {
  (options?: ToastOptions | undefined): ToastId;
  update(id: ToastId, options: Omit<UseToastOptions, 'id'>): void;
  promise: unknown;
  closeAll: () => void;
  close: (id: ToastId) => void;
  isActive: (id: ToastId) => boolean;
}

export const useToast = () => {
  const t = useTranslations();
  const chakraToast = chakraUseToast();
  const isMobile = !useIsDesktop();

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const toast = useCallback(
    (props: ToastOptions) => {
      props = props ?? {};

      const defaultTitle = (() => {
        if (props.status === 'success') {
          return t(`card.It's done!`);
        }
        if (props.status === 'error') {
          return t('card.Something went wrong');
        }
      })();

      const defaultDescription = (() => {
        if (props.status === 'success') {
          return t('card.Your transaction went through successfully');
        }
        if (props.status === 'error') {
          return t(
            `card.Your transaction didn't go through, please try again!`,
          );
        }
      })();

      const containerStyle: UseToastOptions['containerStyle'] = {
        ...props.containerStyle,
        /**
         * Reset containerStyle
         */
        width: isMobile ? '100vw' : '360px',
        marginLeft: '0px',
        marginRight: isMobile ? '0px' : '16px',
        marginTop: isMobile ? '16px' : undefined,
        marginBottom: '16px',
        paddingLeft: isMobile ? '8px' : '0px',
        paddingRight: isMobile ? '8px' : '0px',
        maxWidth: '100vw',
      };

      return chakraToast({
        containerStyle,
        isClosable: true,
        duration: TOAST_DURATION_MS,
        position: isMobile ? 'top' : 'bottom-right',
        title: defaultTitle,
        description: defaultDescription,
        render: (renderProps) => {
          return <Render txId={props.txId} {...renderProps} />;
        },
        ...props,
      });
    },
    [isMobile, chakraToast, t],
  ) as ToastInterface;

  toast.update = chakraToast.update;
  toast.promise = chakraToast.promise;
  toast.close = chakraToast.close;
  toast.closeAll = chakraToast.closeAll;
  toast.isActive = chakraToast.isActive;

  return toast;
};
